import React, { useState, useEffect } from "react";
import { Layout, Table, Select, Input, InputNumber, Button, Tag, Space, Row, Col, Card, message, Popconfirm, Tooltip, Modal, Dropdown } from "antd";
import { FileTextOutlined, PlusOutlined, SearchOutlined, ClearOutlined, FilterOutlined, EditOutlined, DeleteOutlined, SendOutlined, EyeOutlined, CheckCircleOutlined, PrinterOutlined, TruckOutlined, FilePdfOutlined, MoreOutlined } from "@ant-design/icons";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import MRPreviewModal from "../../../components/material_requests/MRPreviewModal";
import MRAcceptNotePreviewModal from "../../../components/material_requests/MRAcceptNotePreviewModal";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getAuthItem } from "../../../services/authService";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Option } = Select;

const RequestList = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    // PDF Preview state
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewMrId, setPreviewMrId] = useState(null);
    const [issuePreviewVisible, setIssuePreviewVisible] = useState(false);
    const [previewIssueMrId, setPreviewIssueMrId] = useState(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState(undefined);
    const [searchFilter, setSearchFilter] = useState("");

    // Applied Filters
    const [appliedStatus, setAppliedStatus] = useState(undefined);
    const [appliedSearch, setAppliedSearch] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        fetchRequests();
    }, [appliedStatus, appliedSearch]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const params = {};
            if (appliedStatus) params.req_status = appliedStatus;
            if (appliedSearch) params.search = appliedSearch;

            const response = await api.get("/inventory/material_request/list.php", { params });
            if (response.data.status === "success") {
                setRequests(response.data.dataset || []);
            } else {
                message.error("Failed to fetch material requests.");
            }
        } catch (error) {
            message.error("Failed to connect to material requests endpoint.");
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        setAppliedStatus(statusFilter);
        setAppliedSearch(searchFilter);
    };

    const handleClear = () => {
        setStatusFilter(undefined);
        setSearchFilter("");
        setAppliedStatus(undefined);
        setAppliedSearch("");
    };

    const handleDelete = async (id) => {
        try {
            const response = await api.post("/inventory/material_request/delete.php", { mr_id: id });
            if (response.data.status === "success") {
                message.success(response.data.message || "Draft request deleted successfully.");
                fetchRequests();
            } else {
                message.error(response.data.message || "Failed to delete draft request.");
            }
        } catch (error) {
            message.error("Error occurred while deleting.");
        }
    };

    const viewDetails = (record) => {
        navigate(`/inventory/material-request/${record.mr_id}/view`);
    };





    const columns = [
        {
            title: "Reference",
            dataIndex: "mr_ref",
            key: "mr_ref",
            render: (text) => <strong style={{ color: 'var(--fluid-primary)' }}>{text}</strong>
        },
        {
            title: "Required Date",
            dataIndex: "required_date",
            key: "required_date",
            render: (date) => dayjs(date).format("YYYY-MM-DD")
        },
        {
            title: "Items Count",
            dataIndex: "total_items",
            key: "total_items",
            align: 'center',
        },
        {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Status",
            dataIndex: "req_status",
            key: "req_status",
            render: (status) => {
                return <Tag color={getStatusColor(status)} style={{ fontWeight: 'bold' }}>{status}</Tag>;
            }
        },
        {
            title: "Requested By",
            dataIndex: "creator_name",
            key: "creator_name",
            render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "-"
        },
        {
            title: "Requested Date",
            dataIndex: "req_dt",
            key: "req_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "-"
        },
        {
            title: "Reviewed By",
            dataIndex: "reviewer_name",
            key: "reviewer_name",
            render: (text, record) => {
                if (record.req_status === 'Pending' || record.req_status === 'Draft') return "-";
                return text ? text.charAt(0).toUpperCase() + text.slice(1) : "System";
            }
        },
        {
            title: "Review Date",
            dataIndex: "req_approved_dt",
            key: "req_approved_dt",
            render: (val, record) => {
                if (record.req_status === 'Pending' || record.req_status === 'Draft') return "-";
                return val ? dayjs.unix(Number(val)).format("YYYY-MM-DD hh:mm:ss A") : "-";
            }
        },
        {
            title: "Actions",
            key: "actions",
            align: 'center',
            width: 80,
            render: (_, record) => {
                const isDraft = record.req_status === 'Draft';
                const canPrintAcceptNote = record.req_status === 'Partially Accepted' || record.req_status === 'Accepted';

                const items = [
                    {
                        key: "view",
                        label: "View Details",
                        icon: <EyeOutlined />,
                        onClick: () => viewDetails(record)
                    },
                    {
                        key: "print_pdf",
                        label: "Print",
                        icon: <PrinterOutlined />,
                        onClick: () => {
                            setPreviewMrId(record.mr_id);
                            setPreviewModalVisible(true);
                        }
                    }
                ];

                if (isDraft) {
                    items.push({
                        key: "edit",
                        label: "Edit Draft",
                        icon: <EditOutlined />,
                        onClick: () => navigate(`/inventory/material-request/${record.mr_id}/items`)
                    });
                    items.push({
                        key: "delete",
                        label: "Delete Draft",
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => {
                            Modal.confirm({
                                title: "Delete Draft",
                                content: "Are you sure you want to delete this draft request?",
                                okText: "Yes, Delete",
                                okType: "danger",
                                cancelText: "No",
                                onOk: () => handleDelete(record.mr_id)
                            });
                        }
                    });
                }

                if (canPrintAcceptNote) {
                    items.push({
                        key: "print_accept",
                        label: "Print",
                        icon: <FilePdfOutlined />,
                        onClick: () => {
                            setPreviewIssueMrId(record.mr_id);
                            setIssuePreviewVisible(true);
                        }
                    });
                }

                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" icon={<MoreOutlined />} className="fluid-dots-btn" />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Material Requests List' },
                ]} />
                <h1 className="fluid-page-title">
                    <FileTextOutlined style={{ marginRight: 12 }} />Material Requests
                </h1>
                {getAuthItem("USERPERMISSION_material_request_create") === "true" && (
                    <div className="hero-actions">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate("/inventory/material-request/create")}
                            className="hero-action-btn"
                        >
                            Create Material Request
                        </Button>
                    </div>
                )}
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card">
                    <div style={{ marginBottom: 20 }}>
                        <Row gutter={[16, 16]} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    placeholder="Select Status"
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    style={{ width: "100%" }}
                                    allowClear
                                >
                                    <Option value="Draft">Draft</Option>
                                    <Option value="Pending">Pending</Option>
                                    <Option value="Approved">Approved</Option>
                                    <Option value="Rejected">Rejected</Option>
                                    <Option value="Partially Accepted">Partially Accepted</Option>
                                    <Option value="Accepted">Accepted</Option>
                                </Select>
                            </Col>

                            <Col xs={24} sm={12} md={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search Reference:</div>
                                <Input
                                    placeholder="MR-XXXXXXXX"
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>

                            <Col xs={24} sm={24} md={4}>
                                <div className="fluid-filter-actions" style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={handleFilter}
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={requests}
                        rowKey="mr_id"
                        loading={loading}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                        }}
                    />
                </Card>
            </Content>

            {/* PDF Preview Modal */}
            {previewModalVisible && (
                <MRPreviewModal
                    mrId={previewMrId}
                    visible={previewModalVisible}
                    onCancel={() => setPreviewModalVisible(false)}
                />
            )}

            {/* Accept Note PDF Preview Modal */}
            {issuePreviewVisible && (
                <MRAcceptNotePreviewModal
                    mrId={previewIssueMrId}
                    visible={issuePreviewVisible}
                    onCancel={() => setIssuePreviewVisible(false)}
                />
            )}

            <Footerdiv />
        </Layout>
    );
};

export default RequestList;
